import React, { useState } from 'react';
import { Staff, Tool } from '../types';
import { PenTool, Edit2, Briefcase, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { StaffModal } from './StaffModal';

interface StaffTableProps {
  staff: Staff[];
  tools: Tool[];
  onStaffUpdate: () => void;
}

interface CollapsibleToolsProps {
  tools: Tool[];
}

function CollapsibleTools({ tools }: CollapsibleToolsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const validTools = tools.filter(tool => tool && tool.id && tool.name);
  const visibleTools = isExpanded ? validTools : validTools.slice(0, 3);
  const remainingCount = validTools.length - 3;

  if (validTools.length <= 3) {
    return (
      <div className="flex flex-wrap gap-2">
        {validTools.map((tool) => (
          <span
            key={tool.id}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
          >
            <PenTool className="w-3 h-3 mr-1" />
            {tool.name}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {visibleTools.map((tool) => (
          <span
            key={tool.id}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
          >
            <PenTool className="w-3 h-3 mr-1" />
            {tool.name}
          </span>
        ))}
        {!isExpanded && remainingCount > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
            }}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <ChevronDown className="w-3 h-3 mr-1" />
            +{remainingCount} more
          </button>
        )}
        {isExpanded && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(false);
            }}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <ChevronUp className="w-3 h-3 mr-1" />
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

export function StaffTable({ staff, tools, onStaffUpdate }: StaffTableProps) {
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [viewMode, setViewMode] = useState(true);

  const handleStaffUpdate = (updatedStaff: Staff) => {
    onStaffUpdate();
    setSelectedStaff(null);
  };

  const getExperienceLevelColor = (level: string) => {
    switch (level) {
      case 'junior':
        return 'bg-green-100 text-green-800';
      case 'mid-level':
        return 'bg-blue-100 text-blue-800';
      case 'senior':
        return 'bg-purple-100 text-purple-800';
      case 'lead':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEmploymentStatusLabel = (status: string) => {
    switch (status) {
      case 'w2':
        return 'W2';
      case '1099_full_time':
        return '1099 Full Time';
      case '1099_part_time':
        return '1099 Part Time';
      default:
        return status;
    }
  };

  const getEmploymentStatusColor = (status: string) => {
    switch (status) {
      case 'w2':
        return 'bg-emerald-100 text-emerald-800';
      case '1099_full_time':
        return 'bg-amber-100 text-amber-800';
      case '1099_part_time':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Experience Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employment Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Year Joined
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tools & Technologies
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {staff.map((member) => (
              <tr
                key={member.id}
                className="hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => {
                  setSelectedStaff(member);
                  setViewMode(true);
                }}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {member.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {member.role}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExperienceLevelColor(member.experience_level)}`}>
                    {member.experience_level.charAt(0).toUpperCase() + member.experience_level.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEmploymentStatusColor(member.employment_status)}`}>
                    <Briefcase className="w-3 h-3 mr-1" />
                    {getEmploymentStatusLabel(member.employment_status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    <Calendar className="w-3 h-3 mr-1" />
                    {member.year_joined}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <CollapsibleTools tools={member.tools} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedStaff(member);
                      setViewMode(false);
                    }}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedStaff && (
        <StaffModal
          staff={selectedStaff}
          tools={tools}
          onClose={() => setSelectedStaff(null)}
          onUpdate={handleStaffUpdate}
          viewOnly={viewMode}
        />
      )}
    </>
  );
}